import { useMemo } from 'react';
import { create } from 'zustand';
import { isLive } from 'modules/CbamCertificates/utils/hedgeMath';
import {
  useSimulationStore,
  isScenarioModified,
} from 'modules/CbamCosts/stores/useSimulationStore';
import {
  isSimulationActive,
  isSimulationModified,
  simulationPayload,
  useCertificateSimulationStore,
} from 'modules/CbamCertificates/stores/useCertificateSimulationStore';
import {
  hedgePayload,
  isHedgeSimulationModified,
  useHedgeSimulationStore,
} from 'modules/CbamCertificates/stores/useHedgeSimulationStore';

/**
 * The planning scenario on both pages (cbam-hedging-scenarios HG4, HG10;
 * cbam-scenario-builder SB1, SB13). Its parts live in their own stores — the
 * verification what-if, the simulated purchases and the hedges — and are saved
 * with it in one call; its price is the price for estimates (`useEuCostsBill`).
 *
 *   applied     the saved scenario, as `/planning-scenarios` returned it (parts
 *               resolved), or null
 *   draft       a new scenario being built, not saved yet: `{ year, startPrice }`, or null
 *   price       the working price: the saved (or starting) one until changed
 *
 * Module state, so it stays applied across the two pages.
 */
const clearParts = () => {
  useSimulationStore.getState().exitSimulation();
  useCertificateSimulationStore.getState().reset();
  useHedgeSimulationStore.getState().reset();
};

export const usePlanningScenarioStore = create((set) => ({
  applied: null,
  draft: null,
  price: null,
  setPrice: (price) => set({ price }),

  /** Apply a saved planning scenario: every present part into its store (HG4). */
  apply: (scenario) => {
    const { verification, certificates, hedges } = scenario?.parts ?? {};
    if (verification) useSimulationStore.getState().loadScenario(verification);
    else useSimulationStore.getState().exitSimulation();
    if (certificates) useCertificateSimulationStore.getState().load(certificates);
    else useCertificateSimulationStore.getState().reset();
    if (hedges) useHedgeSimulationStore.getState().load(hedges);
    else useHedgeSimulationStore.getState().reset();
    set({ applied: scenario, draft: null, price: scenario?.price ?? null });
  },

  /** Start a new scenario for `year` at `price` (the price for estimates now), parts empty. */
  startDraft: (year, price) => {
    clearParts();
    set({ applied: null, draft: { year: Number(year), startPrice: price }, price });
  },

  /** After a save the saved scenario is applied as the server returned it (SB1). */
  markSaved: (scenario) => usePlanningScenarioStore.getState().apply(scenario),

  /** Leave the scenario, or drop the draft: every part store emptied, the browser price back (D6). */
  exit: () => {
    clearParts();
    set({ applied: null, draft: null, price: null });
  },
}));

/** Whether a scenario — applied or a draft — is on for `year`. */
export const scenarioActiveFor = (state, year) =>
  (!!state.applied && Number(state.applied.year) === Number(year)) ||
  (!!state.draft && state.draft.year === Number(year));

/** The scenario price for `year`, or null when none applies (HG10). */
export const planningPriceFor = (state, year) =>
  scenarioActiveFor(state, year) && state.price != null ? Number(state.price) : null;

/**
 * The parts loaded right now, and which of them differ from what the scenario
 * saved (SB1: a part is saved with its scenario, never alone).
 */
export function loadedPartsOf(verification, certificates, hedges) {
  const unsaved = [];
  // An applied verification set with no saved scenario behind it, or a changed one.
  // A saved scenario emptied to no supplier is changed too (isSimulating goes false).
  if ((verification.isSimulating && !verification.scenarioId) || isScenarioModified(verification)) {
    unsaved.push('verification');
  }
  // A loaded part counts as unsaved once changed, emptied included (Clear all
  // on a saved part still changes it); a new one once it has anything in it.
  // `isXModified` already compares against the saved copy when one is loaded.
  if (isSimulationModified(certificates)) unsaved.push('certificates');
  if (isHedgeSimulationModified(hedges)) unsaved.push('hedges');
  return {
    ids: {
      // A saved verification scenario is referenced even when it assumes no
      // supplier fails (its load leaves `isSimulating` false).
      verificationScenarioId: verification.scenarioId ?? null,
      certificateSimulationId: certificates.loaded?._id ?? null,
      hedgeSimulationId: hedges.loaded?._id ?? null,
    },
    names: {
      verification: verification.scenarioName ?? null,
      certificates: certificates.loaded?.name ?? null,
      hedges: hedges.loaded?.name ?? null,
    },
    unsaved,
  };
}

/** The loaded parts, re-read whenever any part store changes. */
export const useLoadedParts = () => {
  const verification = useSimulationStore();
  const certificates = useCertificateSimulationStore();
  const hedges = useHedgeSimulationStore();
  return useMemo(
    () => loadedPartsOf(verification, certificates, hedges),
    [verification, certificates, hedges],
  );
};

/** A planning scenario's three parts, in the order they are listed everywhere. */
export const PART_KEYS = ['verification', 'certificates', 'hedges'];

/**
 * Whether the scenario on screen has work a save would keep: a draft that changes
 * anything (its price, or any part), or an applied scenario whose price or parts
 * differ from what was saved.
 */
export const isScenarioDirty = (state, unsaved) => {
  if (state.draft) {
    return unsaved.length > 0 || Number(state.price) !== Number(state.draft.startPrice);
  }
  if (!state.applied) return false;
  return unsaved.length > 0 || Number(state.price) !== Number(state.applied.price);
};

/** `isScenarioDirty` for the scenario on screen, re-read when any store changes. */
export const useScenarioDirty = () => {
  const applied = usePlanningScenarioStore((s) => s.applied);
  const draft = usePlanningScenarioStore((s) => s.draft);
  const price = usePlanningScenarioStore((s) => s.price);
  const { unsaved } = useLoadedParts();
  return isScenarioDirty({ applied, draft, price }, unsaved);
};

/**
 * The body of one save (cbam-scenario-builder SB1): the scenario and its three parts
 * inline. A part with nothing in it is sent as `null`, which detaches it.
 *
 * @param {{ name: string, year: number, price: number, group?: boolean,
 *           excludedEntityIds?: string[], verificationContext?: object|null }} scenario
 */
export const scenarioSaveBody = ({
  name,
  year,
  price,
  group = false,
  excludedEntityIds = [],
  verificationContext = null,
}) => {
  const verification = useSimulationStore.getState();
  const certificates = useCertificateSimulationStore.getState();
  const hedges = useHedgeSimulationStore.getState();
  // A leg still being filled in is not a hedge (hedgeMath `isLive`); sent, it would
  // fail the whole save.
  const savedLegs = hedges.legs.filter((l) => isLive(l) && Number(l.lockedPrice) > 0);
  return {
    name,
    year: Number(year),
    price: Number(price),
    group,
    excludedEntityIds: group ? excludedEntityIds : [],
    parts: {
      verification: verification.appliedSupplierIds.length
        ? {
            assumeDefaultSupplierIds: [...verification.appliedSupplierIds],
            ...(verificationContext ? { superOrgContext: verificationContext } : {}),
          }
        : null,
      certificates: isSimulationActive(certificates) ? simulationPayload(certificates) : null,
      hedges: savedLegs.length ? hedgePayload({ legs: savedLegs }) : null,
    },
  };
};

export default usePlanningScenarioStore;
